import { useEffect, useMemo, useRef, useState } from "react";
import { getJson, useJson, entityPath, go, nf, TYPE_LABEL, OBS_TYPES, Chip, IdBadge, sectionLabel, card, control } from "./shared";
import { vizTokens } from "./Charts";
import { StixExportLink } from "./Stix";

// Side-by-side comparison of 2–4 threat groups, campaigns or software.
// Route: #/kb/compare/<id>,<id>,…  (ATT&CK ids or STIX ids)

const pct = (x) => `${Math.round(x * 100)}%`;
const comparePath = (ids) => `#/kb/compare/${ids.map(encodeURIComponent).join(",")}`;

function AddEntity({ ids, ui }) {
  const { B, fs, TypeIcon, getConf } = ui;
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const ref = useRef(null);
  useEffect(() => {
    const term = q.trim();
    if (!term) { setItems([]); return; }
    const t = setTimeout(() => {
      getJson(`/api/kb/search?q=${encodeURIComponent(term)}`)
        .then(d => setItems((d.items || []).filter(s => !OBS_TYPES.has(s.type) && ["intrusion-set", "campaign", "malware", "tool"].includes(s.type)).slice(0, 8)))
        .catch(() => setItems([]));
    }, 150);
    return () => clearTimeout(t);
  }, [q]);
  if (ids.length >= 4) return null;
  return (
    <div ref={ref} style={{ position: "relative", flex: "1 1 240px", maxWidth: 360 }}>
      <input value={q} onChange={e => setQ(e.target.value)} placeholder="Add a group, campaign or malware…" style={{ ...control(ui), width: "100%", boxSizing: "border-box" }} />
      {q.trim() && items.length > 0 && (
        <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 20, background: B.bgPanel, border: `1px solid ${B.borderLight}`, borderRadius: 8, boxShadow: "0 12px 32px rgba(0,0,0,0.35)" }}>
          {items.map(s => (
            <div key={s.id} onMouseDown={() => { setQ(""); go(comparePath([...ids, s.attackId || s.id])); }} style={{ display: "flex", gap: 8, alignItems: "center", padding: "8px 12px", cursor: "pointer", fontSize: fs(12), color: B.text }}>
              <TypeIcon type={s.type} color={getConf(s.type).color} size={13} />
              <span style={{ flex: 1 }}>{s.name}</span>
              <span style={{ color: B.textDim, fontSize: fs(10) }}>{TYPE_LABEL[s.type]}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function KbCompare({ ids, ui }) {
  const { B, fs, TypeIcon, getConf } = ui;
  const t = vizTokens(B);
  const ready = ids.length >= 2;
  const { data, error, loading } = useJson(ready ? `/api/kb/compare?ids=${ids.map(encodeURIComponent).join(",")}` : null);
  const [sharedOnly, setSharedOnly] = useState(true);

  const techniquesByTactic = useMemo(() => {
    if (!data) return [];
    const order = (data.tactics || []).map(x => x.name);
    const map = new Map(order.map(n => [n, []]));
    for (const tq of data.techniques) {
      if (sharedOnly && tq.sharedBy < 2) continue;
      for (const tac of tq.tactics?.length ? tq.tactics : ["Other"]) {
        if (!map.has(tac)) map.set(tac, []);
        map.get(tac).push(tq);
      }
    }
    return [...map.entries()].filter(([, l]) => l.length).map(([n, l]) => [n, l.sort((a, b) => b.sharedBy - a.sharedBy || a.name.localeCompare(b.name))]);
  }, [data, sharedOnly]);

  const remove = (i) => go(comparePath(ids.filter((_, j) => j !== i)));

  return (
    <div>
      <h2 style={{ fontSize: fs(22), fontWeight: 900, margin: "4px 0 6px", color: B.text }}>Compare</h2>
      <p style={{ color: B.textMuted, fontSize: fs(13), margin: "0 0 16px", lineHeight: 1.6 }}>
        Put up to four groups, campaigns or malware side by side. Overlap is measured with Jaccard similarity (shared ÷ combined). A high overlap is a lead worth investigating, not proof two actors are the same — many techniques and tools are used by everyone.
      </p>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 18 }}>
        {(data?.entities || []).map((e, i) => (
          <span key={e.summary.id} style={{ display: "inline-flex", alignItems: "center", gap: 6, ...card(ui), borderRadius: 20, padding: "5px 6px 5px 12px", fontSize: fs(12), color: B.text }}>
            <TypeIcon type={e.summary.type} color={getConf(e.summary.type).color} size={13} />
            <b>{e.summary.name}</b>
            <button onClick={() => remove(i)} aria-label={`Remove ${e.summary.name}`} style={{ background: "none", border: "none", color: B.textDim, cursor: "pointer", fontSize: fs(14), padding: "0 4px" }}>×</button>
          </span>
        ))}
        <AddEntity ids={ids} ui={ui} />
        {data?.entities?.length >= 2 && <span style={{ marginLeft: "auto" }}><StixExportLink ui={ui} href={`/api/kb/stix/compare?ids=${data.entities.map(e => encodeURIComponent(e.summary.attackId || e.summary.id)).join(",")}`} title="Download the compared entities and everything at least two of them share as a STIX 2.1 bundle" /></span>}
      </div>

      {!ready && <div style={{ ...card(ui), padding: 18, color: B.textMuted, fontSize: fs(13) }}>Add at least two to compare. Try the "Compare" button on a group's profile, or search above.</div>}
      {error && <div style={{ color: "#dc2626", fontSize: fs(12) }}>{error}</div>}
      {loading && <div style={{ color: B.textDim, fontSize: fs(12) }}>Loading…</div>}

      {data && (
        <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
          {/* Who's being compared */}
          <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, 220px), 1fr))`, gap: 12 }}>
            {data.entities.map(e => (
              <a key={e.summary.id} href={entityPath(e.summary)} style={{ ...card(ui), padding: 16, textDecoration: "none", display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <TypeIcon type={e.summary.type} color={getConf(e.summary.type).color} size={15} />
                  <span style={{ fontSize: fs(15), fontWeight: 800, color: B.text, flex: 1 }}>{e.summary.name}</span>
                  <IdBadge id={e.summary.attackId} ui={ui} />
                </div>
                <div style={{ fontSize: fs(12), color: B.textMuted }}>{TYPE_LABEL[e.summary.type]}{e.origin.length ? ` · attributed to ${e.origin.join(", ")}` : ""}</div>
                <div style={{ fontSize: fs(12), color: B.textDim }}>{nf(e.techniqueCount)} techniques · {nf(e.softwareCount)} software{e.victims ? ` · ${nf(e.victims)} claimed victims` : ""}</div>
              </a>
            ))}
          </div>

          {/* Overlap — a number, not a chart */}
          <div>
            <div style={{ ...sectionLabel(ui), marginBottom: 10 }}>Overlap</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 240px), 1fr))", gap: 12 }}>
              {data.pairs.map(p => (
                <div key={`${p.a}-${p.b}`} style={{ ...card(ui), padding: "14px 16px" }}>
                  <div style={{ fontSize: fs(12), color: B.textMuted, marginBottom: 6 }}>{data.entities[p.a].summary.name} ↔ {data.entities[p.b].summary.name}</div>
                  <div style={{ display: "flex", gap: 22, alignItems: "baseline" }}>
                    <div><div style={{ fontSize: fs(26), fontWeight: 900, color: B.text }}>{pct(p.techniques)}</div><div style={{ fontSize: fs(11), color: B.textDim }}>technique overlap</div></div>
                    <div><div style={{ fontSize: fs(26), fontWeight: 900, color: B.text }}>{pct(p.software)}</div><div style={{ fontSize: fs(11), color: B.textDim }}>software overlap</div></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Shared software */}
          {data.software.some(s => s.sharedBy > 1) && (
            <div>
              <div style={{ ...sectionLabel(ui), marginBottom: 10 }}>Software used by more than one</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {data.software.filter(s => s.sharedBy > 1).sort((a, b) => b.sharedBy - a.sharedBy || a.name.localeCompare(b.name)).map(s => (
                  <a key={s.id} href={entityPath(s)} style={{ textDecoration: "none" }}>
                    <Chip ui={ui} color={getConf(s.type).color} title={data.entities.filter((_, i) => s.has[i]).map(e => e.summary.name).join(", ")}>{s.name} · {s.sharedBy}/{data.entities.length}</Chip>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Technique matrix: rows = techniques, columns = entities; a filled dot means "uses". */}
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
              <div style={sectionLabel(ui)}>Techniques</div>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: fs(12), color: B.textMuted, cursor: "pointer" }}>
                <input type="checkbox" checked={sharedOnly} onChange={e => setSharedOnly(e.target.checked)} /> Only techniques used by more than one
              </label>
            </div>
            {techniquesByTactic.length === 0 && <div style={{ ...card(ui), padding: 16, fontSize: fs(13), color: B.textMuted }}>No shared techniques.</div>}
            <div style={{ ...card(ui), overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: fs(12), minWidth: 420 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", padding: "10px 14px", color: B.textDim, fontWeight: 700, borderBottom: `1px solid ${B.border}` }}>Technique</th>
                    {data.entities.map(e => <th key={e.summary.id} style={{ padding: "10px 10px", color: B.text, fontWeight: 700, borderBottom: `1px solid ${B.border}`, textAlign: "center", whiteSpace: "nowrap" }}>{e.summary.name}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {techniquesByTactic.map(([tac, list]) => (
                    [
                      <tr key={`h-${tac}`}><td colSpan={data.entities.length + 1} style={{ padding: "12px 14px 6px", ...sectionLabel(ui), fontSize: fs(10) }}>{tac}</td></tr>,
                      ...list.map(tq => (
                        <tr key={`${tac}-${tq.id}`}>
                          <td style={{ padding: "6px 14px", borderTop: `1px solid ${B.border}` }}>
                            <a href={entityPath(tq)} style={{ color: B.textMuted, textDecoration: "none" }}>
                              {tq.parentName ? <span style={{ color: B.textDim }}>{tq.parentName}: </span> : null}{tq.name}
                            </a> <span style={{ color: B.textDim, fontFamily: "monospace", fontSize: fs(10) }}>{tq.attackId}</span>
                          </td>
                          {tq.has.map((h, i) => (
                            <td key={i} style={{ textAlign: "center", borderTop: `1px solid ${B.border}` }} aria-label={h ? "uses" : "not reported"}>
                              <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: h ? t.series1 : "transparent", border: `1.5px solid ${h ? t.series1 : B.borderLight}` }} />
                            </td>
                          ))}
                        </tr>
                      )),
                    ]
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Victimology, if any of them has leak-site data */}
          {data.entities.some(e => e.victims) && (
            <div>
              <div style={{ ...sectionLabel(ui), marginBottom: 10 }}>Claimed victims (ransomware.live)</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))", gap: 12 }}>
                {data.entities.map(e => (
                  <div key={e.summary.id} style={{ ...card(ui), padding: "14px 16px", fontSize: fs(12) }}>
                    <div style={{ color: B.text, fontWeight: 800, marginBottom: 8 }}>{e.summary.name} <span style={{ color: B.textDim, fontWeight: 500 }}>· {nf(e.victims)}</span></div>
                    {e.victims ? (
                      <>
                        <div style={{ color: B.textDim, marginBottom: 4 }}>Top countries</div>
                        {e.topCountries.map(c => <div key={c.iso2} style={{ display: "flex", justifyContent: "space-between", color: B.textMuted }}><span>{c.name}</span><span style={{ fontVariantNumeric: "tabular-nums", color: B.text }}>{nf(c.n)}</span></div>)}
                        <div style={{ color: B.textDim, margin: "10px 0 4px" }}>Top sectors</div>
                        {e.topSectors.map(s => <div key={s.sector} style={{ display: "flex", justifyContent: "space-between", color: B.textMuted }}><span>{s.sector}</span><span style={{ fontVariantNumeric: "tabular-nums", color: B.text }}>{nf(s.n)}</span></div>)}
                      </>
                    ) : <div style={{ color: B.textDim }}>No leak-site data.</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export { comparePath };
